
import { createLogger } from 'common/logger';
import { Color, Mesh, Object3D, Material } from 'three';
import { ComputedValues } from '../types/object';
import { TransitionTarget, TransitionTargetResolved } from '../types/animations';
import { createIdle } from '../tweens/idle';
import { TimelineWithPosition, combineTimelines } from './combine';
import { translateDomProperty } from '../tweens/dom';
import { translateObject3D } from '../tweens/object3d';
import { translateColor } from '../tweens/color';
import { AnimationEvent } from '../types/animations';
import { translateMaterial } from '../tweens/material';
import { getDefaultTimelineVars } from '../tweens/_common';

// should be always enabled
const logger = createLogger('[ThreeGsapEvents:run]', 'console');

type RunEventOptions = {
    // used to find objects in children by name
    root?: Object3D | HTMLElement;
    // used to store computed values that are referenced in the transitions by string keys
    computed?: ComputedValues;
    // used to resolve target by transition target (if it's a string)
    resolveTarget?: (target: string) => TransitionTargetResolved | null;

    immediateRender?: boolean;

    rootTimelineVars?: gsap.TimelineVars;

    log?: boolean;
};

export function runEvent(event: AnimationEvent, options?: RunEventOptions): gsap.core.Timeline {
    const { root, computed, resolveTarget, rootTimelineVars } = options || {};
    const resultComputed: ComputedValues = computed || {};
    const log = (...args: any[]) => options?.log ? logger.log(...args) : null;

    const tls: TimelineWithPosition[] = event.transitions.map(t => {
        const {
            target, from, to,
            duration = 1,
            easing: ease = 'none',
            cycle = false,
            loop = 0,
            position,
            delay = 0,
            reset,
            overwrite,
        } = t;

        if (!from && !to) {
            return {
                tl: createIdle({ vars: { duration } }),
                position,
            };
        }

        const getTarget = (tt: TransitionTarget): TransitionTargetResolved => {
            if (typeof tt !== 'string') {
                return tt;
            }

            if (resolveTarget) {
                const resolved = resolveTarget(tt);
                if (resolved) {
                    return resolved;
                }
            }

            if (tt.startsWith('#') || tt.startsWith('.')) {
                const r = root instanceof HTMLElement
                    ? root
                    : document;
                const resolved = r.querySelector<HTMLElement>(tt);
                if (resolved) {
                    return resolved;
                }
            }

            if (root instanceof Object3D) {
                const resolved = root.getObjectByName(tt);
                if (resolved) {
                    return resolved;
                }
            }

            logger.warn('Target not resolved:', tt);
            return null;
        };

        // collect all objects to animate
        const objs = (
            Array.isArray(target)
                ? target.map(getTarget)
                : [getTarget(target)]
        ).filter(Boolean);

        if (!objs?.length) {
            logger.warn('Transition targets not resolved, skipping animation.\n', target, root);
            return null;
        }

        let res: gsap.core.Timeline;

        const timelineVars = {
            ...getDefaultTimelineVars(),
            delay,
        };

        if (options?.immediateRender != null) {
            timelineVars.defaults = {
                ...timelineVars.defaults,
                immediateRender: options.immediateRender,
            };
        }

        const baseOptions = {
            from: from,
            to: to,
            reset,
            vars: {
                duration,
                ease,
                repeat: cycle ? -1 : (loop || 0),
                yoyo: cycle,
                overwrite,
            },
            timelineVars,
            computed: resultComputed,
        };

        const isMaterialTransition = !!(from?.material || to?.material);
        const is3DTransition = !!(from?.position || to?.position || from?.scale || to?.scale || from?.rotation || to?.rotation);
        const isColorTransition = !!(from?.color || to?.color);

        // Check each object for compatibility with the transition

        objs.forEach(obj => {
            if (obj instanceof HTMLElement || typeof obj === 'string') {
                res = translateDomProperty(obj, { tl: res, ...baseOptions });
                log('translateDomProperty', obj, t);
                return;
            }

            if (obj instanceof Object3D && is3DTransition) {
                res = translateObject3D(
                    obj,
                    {
                        tl: res,
                        ...baseOptions,
                    },
                );
                log('translateObject3D', obj, t);
            }

            if (obj instanceof Mesh && obj.isMesh && isMaterialTransition) {
                res = translateMaterial(
                    obj.material,
                    {
                        tl: res,
                        ...baseOptions,
                    },
                );
                log('translateMaterial (mesh)', obj, t);
            }

            if (obj instanceof Material && obj.isMaterial && isMaterialTransition) {
                res = translateMaterial(
                    obj,
                    {
                        tl: res,
                        ...baseOptions,
                    },
                );
                log('translateMaterial', obj, t);
            }

            if (obj instanceof Color && obj.isColor && isColorTransition) {
                res = translateColor(
                    obj,
                    {
                        tl: res,
                        ...baseOptions,
                    },
                );
                log('translateColor', obj, t);
            }
        });

        return { tl: res, position };
    }).filter(r => !!r.tl);

    Object.entries(resultComputed).forEach(([key, value]) => {
        if (value === ComputedValues.UnresolvedKey) {
            logger.warn('runEvent: Unresolved key for transition:', key);
        }
    });

    log('runEvent result', tls, resultComputed);

    return combineTimelines(rootTimelineVars, ...tls);
}
